import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import {
  FiActivity, FiAlertCircle, FiAlertTriangle, FiArrowRight, FiCheckCircle, FiClock,
  FiInbox, FiMessageSquare, FiUserX,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import i18n from '../../../i18n';
import BASE_URL from '../../../config/config';
import { cabecerasAuth, claves } from '../../../queries/cliente';

const departmentName = (department) => (
  i18n.t(`dashboardStaff.departamentos.${department}`, { defaultValue: null })
  || String(department || i18n.t('dashboardStaff.sinAsignar')).replace(/[_-]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
);

const formatDuration = (seconds) => {
  if (!seconds) return i18n.t('dashboardStaff.sinDatos');
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const d = i18n.t('dashboardStaff.dias');
  const h = i18n.t('dashboardStaff.horas');
  const m = i18n.t('dashboardStaff.minutos');
  if (days) return `${days} ${d} ${hours} ${h}`;
  if (hours) return `${hours} ${h} ${minutes} ${m}`;
  return `${Math.max(minutes, 1)} ${m}`;
};

const formatUpdatedAt = (dateValue) => {
  const date = new Date(dateValue);
  const elapsed = Date.now() - date.getTime();
  const minutes = Math.max(0, Math.floor(elapsed / 60000));
  if (minutes < 1) return i18n.t('dashboardStaff.ahoraMismo');
  if (minutes < 60) return i18n.t('dashboardStaff.hace', { tiempo: `${minutes} ${i18n.t('dashboardStaff.minutos')}` });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return i18n.t('dashboardStaff.hace', { tiempo: `${hours} ${i18n.t('dashboardStaff.horas')}` });
  return date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
};

const statusLabel = (status) => i18n.t(`dashboardStaff.estados.${status}`, { defaultValue: status });

const olderThan = (ticket, hours) => Date.now() - new Date(ticket.createdAt).getTime() >= hours * 60 * 60 * 1000;

const DashboardStaff = ({ tokenInfo, onLinkClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  /**
   * Abre el gestor de tickets con un filtro ya aplicado. El gestor lee sus
   * filtros de localStorage al montar, así que basta con dejarlos escritos
   * (reemplazando los anteriores) antes de navegar.
   */
  const verTicketsCon = (filtros) => {
    try {
      localStorage.setItem('ticketManagerFiltros', JSON.stringify(filtros));
    } catch (err) {
      // Almacenamiento bloqueado: se abre el gestor sin filtrar
    }
    if (onLinkClick) onLinkClick();
    navigate('/dashboard/tickets');
  };

  // Tres consultas independientes y cacheadas: el gestor de tickets reutiliza
  // las dos primeras sin volver a pedirlas al servidor.
  const [consultaAbiertos, consultaArchivados, consultaStats] = useQueries({
    queries: [
      {
        queryKey: claves.ticketsTodos(),
        queryFn: async () => (await axios.get(`${BASE_URL}/tickets/total/?page=1&limit=1000`, cabecerasAuth())).data.tickets || [],
      },
      {
        queryKey: claves.ticketsArchivados(),
        queryFn: async () => (await axios.get(`${BASE_URL}/tickets/total-archivados/?page=1&limit=1000`, cabecerasAuth())).data.tickets || [],
      },
      {
        queryKey: claves.estadisticasTickets(),
        queryFn: async () => (await axios.get(`${BASE_URL}/api/admin/stats/tickets`, cabecerasAuth())).data,
      },
    ],
  });

  const consultas = [consultaAbiertos, consultaArchivados, consultaStats];

  if (consultas.some((c) => c.isError)) {
    return <div className="staff-dashboard-status">{t('dashboardStaff.errorCargar')}</div>;
  }
  if (consultas.some((c) => c.isPending)) {
    return <div className="staff-dashboard-status">{t('dashboardStaff.cargando')}</div>;
  }

  const tickets = [...(consultaAbiertos.data || []), ...(consultaArchivados.data || [])];
  const stats = consultaStats.data;
  const openTickets = tickets.filter((ticket) => ['abierto', 'retenido'].includes(ticket.estado_del_ticket));
  const pendingTickets = openTickets.filter((ticket) => ticket.estado_del_chat === 'sin_responder');
  const unassignedTickets = openTickets.filter((ticket) => ticket.usuario_encargado === 'sin_asignar');
  const resolvedTickets = tickets.filter((ticket) => ticket.estado_del_ticket === 'cerrado');
  const recentTickets = [...tickets].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).slice(0, 6);
  const categoryCounts = tickets.reduce((counts, ticket) => {
    const category = ticket.dpto || 'no_especificado';
    counts[category] = (counts[category] || 0) + 1;
    return counts;
  }, {});
  const categories = Object.entries(categoryCounts).sort(([, first], [, second]) => second - first);
  const maxCategoryCount = categories[0]?.[1] || 1;
  const pendingOver24Hours = pendingTickets.filter((ticket) => olderThan(ticket, 24)).length;
  const backlog24Hours = openTickets.filter((ticket) => olderThan(ticket, 24)).length;
  const backlog3Days = openTickets.filter((ticket) => olderThan(ticket, 72)).length;
  const backlog7Days = openTickets.filter((ticket) => olderThan(ticket, 168)).length;

  const urgentTickets = openTickets.filter((ticket) => ticket.prioridad === 'urgente');

  // Estados que cuentan como "abierto" en este panel
  const ABIERTOS = ['abierto', 'retenido'];

  const metrics = [
    {
      label: t('dashboardStaff.ticketsTotales'), value: tickets.length, icon: FiInbox, tone: 'blue',
      detail: t('dashboardStaff.historicoCompleto'),
      filtros: {},
    },
    {
      label: t('dashboardStaff.ticketsAbiertos'), value: openTickets.length, icon: FiActivity, tone: 'purple',
      detail: t('dashboardStaff.abiertosYEnEspera'),
      filtros: { selectedEstados: ABIERTOS },
    },
    {
      label: t('dashboardStaff.ticketsUrgentes'), value: urgentTickets.length, icon: FiAlertTriangle, tone: 'red',
      detail: t('dashboardStaff.urgentesAbiertos'),
      filtros: { selectedEstados: ABIERTOS, selectedPrioridades: ['urgente'] },
    },
    {
      label: t('dashboardStaff.pendientesRespuesta'), value: pendingTickets.length, icon: FiMessageSquare, tone: 'amber',
      detail: t('dashboardStaff.requierenAtencion'),
      filtros: { selectedEstados: ABIERTOS, selectedChats: ['sin_responder'] },
    },
    {
      label: t('dashboardStaff.ticketsResueltos'), value: resolvedTickets.length, icon: FiCheckCircle, tone: 'green',
      detail: t('dashboardStaff.cerradosCorrectamente'),
      filtros: { selectedEstados: ['cerrado'] },
    },
    // El tiempo medio es una métrica, no una lista: no lleva a ningún filtro
    {
      label: t('dashboardStaff.tiempoMedio'), value: formatDuration(stats.avgResolutionTimeSeconds), icon: FiClock, tone: 'slate',
      detail: t('dashboardStaff.mediaGlobalCierre'), compact: true,
    },
  ];

  return (
    <div className="staff-dashboard">
      <div className="staff-dashboard-header">
        <div>
          <span className="staff-eyebrow">{t('dashboardStaff.centroOperaciones')}</span>
          <h1>{t('dashboardStaff.actividadSoporte')}</h1>
          <p>{t('dashboardStaff.resumenTexto')}</p>
        </div>
        <Link to="/dashboard/tickets" className="staff-dashboard-action" onClick={onLinkClick}>
          {t('dashboardStaff.gestionarTickets')} <FiArrowRight />
        </Link>
      </div>

      <section className="staff-metrics" aria-label={t('dashboardStaff.indicadoresPrincipales')}>
        {metrics.map(({ label, value, icon: Icon, tone, detail, compact, filtros }) => {
          const contenido = (
            <>
              <div className="staff-metric-top"><span>{label}</span><Icon /></div>
              <strong className={compact ? 'is-duration' : ''}>{value}</strong>
              <small>{detail}</small>
            </>
          );

          // Las tarjetas con filtro abren el gestor ya filtrado; el tiempo
          // medio no tiene lista asociada y se queda como tarjeta estática.
          return filtros ? (
            <button
              key={label}
              type="button"
              className={`staff-metric staff-metric-${tone} staff-metric--enlace`}
              onClick={() => verTicketsCon(filtros)}
              title={t('dashboardStaff.verTicketsFiltrados')}
            >
              {contenido}
            </button>
          ) : (
            <article key={label} className={`staff-metric staff-metric-${tone}`}>
              {contenido}
            </article>
          );
        })}
      </section>

      <section className="staff-insights-grid">
        <article className="staff-panel staff-categories-panel">
          <div className="staff-panel-heading">
            <div><span className="staff-eyebrow">{t('dashboardStaff.distribucion')}</span><h2>{t('dashboardStaff.ticketsPorCategoria')}</h2></div>
            <span className="staff-panel-total">{t('dashboardStaff.total', { count: tickets.length })}</span>
          </div>
          <div className="staff-category-list">
            {categories.length ? categories.map(([category, count]) => (
              <div className="staff-category-row" key={category}>
                <span>{departmentName(category)}</span>
                <div className="staff-category-bar"><i style={{ width: `${(count / maxCategoryCount) * 100}%` }} /></div>
                <strong>{count}</strong>
              </div>
            )) : <p className="staff-empty-copy">{t('dashboardStaff.sinTickets')}</p>}
          </div>
        </article>

        <article className="staff-panel staff-queue-panel">
          <div className="staff-panel-heading">
            <div><span className="staff-eyebrow">{t('dashboardStaff.colaDeTrabajo')}</span><h2>{t('dashboardStaff.requierenAtencionTitulo')}</h2></div>
            <FiAlertCircle className="staff-heading-icon" />
          </div>
          <div className="staff-queue-summary">
            <div className="staff-queue-highlight"><FiUserX /><div><strong>{unassignedTickets.length}</strong><span>{t('dashboardStaff.sinAsignarLabel')}</span></div></div>
            <div className="staff-queue-highlight"><FiMessageSquare /><div><strong>{pendingTickets.length}</strong><span>{t('dashboardStaff.pendientesDeRespuestaLabel')}</span></div></div>
            <p>{pendingOver24Hours > 0
              ? t('dashboardStaff.pendientesMas24h', { count: pendingOver24Hours })
              : t('dashboardStaff.sinPendientesMas24h')}</p>
          </div>
          <div className="staff-backlog">
            <span className="staff-backlog-label">{t('dashboardStaff.backlogLabel')}</span>
            <div>
              <span><strong>{backlog24Hours}</strong>{t('dashboardStaff.mas24h')}</span>
              <span><strong>{backlog3Days}</strong>{t('dashboardStaff.mas3dias')}</span>
              <span><strong>{backlog7Days}</strong>{t('dashboardStaff.mas7dias')}</span>
            </div>
          </div>
        </article>
      </section>

      <section className="staff-panel staff-recent-panel ticket-table-surface">
        <div className="staff-panel-heading">
          <div><span className="staff-eyebrow">{t('dashboardStaff.actividadReciente')}</span><h2>{t('dashboardStaff.ultimasActualizaciones')}</h2></div>
          <Link to="/dashboard/tickets" onClick={onLinkClick}>{t('dashboardStaff.verTodos')} <FiArrowRight /></Link>
        </div>
        <div className="staff-recent-table" role="table" aria-label={t('dashboardStaff.ticketsActualizadosRecientemente')}>
          <div className="staff-recent-head" role="row"><span>{t('dashboardStaff.ticket')}</span><span>{t('dashboardStaff.departamento')}</span><span>{t('dashboardStaff.estado')}</span><span>{t('dashboardStaff.responsable')}</span><span>{t('dashboardStaff.actualizado')}</span></div>
          {recentTickets.map((ticket) => (
            <Link key={ticket.identificador} to={`/ticket/${ticket.identificador}`} className="staff-recent-row" role="row" onClick={onLinkClick}>
              <span className="staff-ticket-subject"><b>#{ticket.identificador}</b><em>{ticket.asunto}</em></span>
              <span>{departmentName(ticket.dpto)}</span>
              <span><i className={`staff-status staff-status-${ticket.estado_del_ticket}`}>{statusLabel(ticket.estado_del_ticket)}</i></span>
              <span>{ticket.usuario_encargado === 'sin_asignar' ? t('dashboardStaff.sinAsignarLabel') : `@${ticket.usuario_encargado}`}</span>
              <time dateTime={ticket.updatedAt}>{formatUpdatedAt(ticket.updatedAt)}</time>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default DashboardStaff;
